import {
  Typography,
  Box,
  Container,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Link } from 'react-router-dom';

import { AppHeader } from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-header';
import {
  portalOrder,
  projectPortalMap,
} from '@eng-mono/atlassian/jira/mojira-bugtracker/util-issue-list-fe';
import { useDocumentTitle } from '@eng-mono/atlassian/shared/frontend-ui-contexts';

import styles from './home-page.module.scss';

interface HomePageProps {
  siteUrl: string;
}

export function HomePage(props: HomePageProps) {
  useDocumentTitle('Mojira - Home');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {/* Header */}
      <AppHeader loginDestination={`${props.siteUrl}/servicedesk`} />

      {/* Main Content */}
      <Container maxWidth="md" component={'main'}>
        {/* Introduction Section */}
        <Paper
          elevation={3}
          sx={{ marginTop: 3 }}
          className={styles['introduction-paper']}
        >
          <Box sx={{ backgroundColor: '#c90000', padding: isMobile ? 1 : 2 }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="white">
              Introduction
            </Typography>
          </Box>
          <Box sx={{ padding: isMobile ? 2 : 3 }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
              Welcome to Mojang's Official Issue Tracker!
            </Typography>
            <Typography variant="body1" gutterBottom>
              Using this issue tracker, you can report bugs you've found in any
              of our games.{' '}
              <b>
                Please note: This site is not for help, support, or suggestions.
              </b>{' '}
              Before you report a bug in any of our games, please perform a
              search first to see if someone has already contacted us about it
              to save yourself time and effort.
            </Typography>
            <Typography
              variant={isMobile ? 'subtitle1' : 'h6'}
              gutterBottom
              sx={{ mt: 3 }}
            >
              Our Games - Click on the Game's Logo
            </Typography>
            <Divider sx={{ marginBottom: 2 }} />
            <Typography variant="body1" gutterBottom>
              Here is a list of our games that use this issue tracker.{' '}
              <b>
                Please select the correct game before you begin creating a
                report as each site has useful & relevant information made ready
                for you and we have tried our best to make things as simple to
                use as possible.
              </b>
            </Typography>

            {/* Game Logos Placeholder */}
            <main><Box className={styles['game-logos-grid']}>
              {/* loop over projectPortalMap */}
              {portalOrder.map((id) => (
                <Link
                  key={projectPortalMap[id].project}
                  to={'/browse/' + projectPortalMap[id].project}
                >
                  {(projectPortalMap[id].portalLogoImageUrl && (
                    <img
                      src={projectPortalMap[id].portalLogoImageUrl}
                      alt={projectPortalMap[id].portalName + ' logo'}
                    />
                  )) || (
                      <span className={styles['logo-with-image']}>
                        <img
                          src={projectPortalMap[id].portalIconUrl}
                          alt={projectPortalMap[id].portalName + ' logo'}
                        />
                        <Typography variant="h6" gutterBottom>
                          {projectPortalMap[id].portalName}
                        </Typography>
                      </span>
                    )}
                </Link>
              ))}
            </Box></main>

            <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
              Tips for Making Awesome Reports
            </Typography>
            <Divider sx={{ marginBottom: 2 }} />
            <Box sx={{ pl: isMobile ? 2 : 3 }}>
              <ol>
                <li>
                  <Typography variant="body2" gutterBottom>
                    Submit ONE issue per report please, it's easier for us to
                    follow and manage!
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" gutterBottom>
                    Please provide a (to the point) summary of your problem.
                    Avoid one worded summaries and things like "help!!!".
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" gutterBottom>
                    The description should contain details of the problem and
                    how to reproduce it, so that we can more easily track down
                    the problem and try to fix it.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" gutterBottom>
                    Please search before creating a report to make sure your
                    issue hasn't already been reported. If it has, please join
                    the discussion there!
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" gutterBottom>
                    If you've discovered an exploit or a security issue, please
                    create a Private report that only yourself and Mojang will
                    be able to see.
                  </Typography>
                </li>
              </ol>
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  );
}

export default HomePage;
